import {
	ApolloClient,
	InMemoryCache,
	createHttpLink,
	HttpLink,
} from "@apollo/client";
import { sha256_hex } from "@core/helpers/sha256";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { setContext } from "@apollo/client/link/context";
import { detect_site_url } from "@core/helpers/api-headers";
import { is_localhost } from "@core/helpers/url";
import { is_mobile_device } from "@core/helpers/device";
import { get_cookie } from "@core/helpers/cookie";
import { has_ssr } from "./render-type";
import { get_ip_server } from "@core/helpers/server/url";
import { get_current_locate_from_cookie } from "@core/helpers/locate";
import { get_dev_mode, dev_mode_headers } from "@core/helpers/dev-mode";

// const uri = has_ssr() ? (process.env.GRAPHQL_URL || process.env.NEXT_PUBLIC_GRAPHQL_URL) : '/web-api';
const uri = has_ssr()
	? process.env.LOCAL_GRAPHQL_URL ||
	process.env.GRAPHQL_URL ||
	process.env.NEXT_PUBLIC_GRAPHQL_URL
	: process.env.GRAPHQL_URL || process.env.NEXT_PUBLIC_GRAPHQL_URL;

// chawk-bazar / ciseco (core-issues "graphql.js + timeout"): a server request to a backend that never answers waited for
// the platform fetch timeout, so the page (and every render awaiting the same cache key) hung. On the server each
// request is aborted after GRAPHQL_TIMEOUT_MS (default 30 s) and the service resolves `{ status: false }` as for any
// network error. The browser keeps fetch's own behaviour.
export const SERVER_GRAPHQL_TIMEOUT_MS = Number(process.env.GRAPHQL_TIMEOUT_MS) > 0 ? Number(process.env.GRAPHQL_TIMEOUT_MS) : 30000;

const timed_fetch = (input, init = {}) => {
	if (!has_ssr() || typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
		return fetch(input, init);
	}
	const timeout = AbortSignal.timeout(SERVER_GRAPHQL_TIMEOUT_MS);
	const signal = init?.signal && typeof AbortSignal.any === "function" ? AbortSignal.any([init.signal, timeout]) : timeout;
	return fetch(input, { ...init, signal });
};

const http_link = (headers = {}) =>
	createHttpLink({
		uri,
		headers,
		fetch: timed_fetch,
	});

// Persisted-query id. Same hex as the former CryptoJS.SHA256(input).toString(Hex), without bundling crypto-js
// into every page (jument).
export function sha256(input) {
	return sha256_hex(input);
}

const link_chain = (headers = {}) =>
	createPersistedQueryLink({
		sha256,
		useGETForHashedQueries: true,
	}).concat(
		new HttpLink({
			uri,
			headers,
			fetch: timed_fetch,
		})
	);

const main_link = setContext(async (_, { headers }) => {
	const token = await get_cookie("_token");
	// server-side calls reach the API from a docker/server address, so pass on who the visitor really is.
	// never sent from the browser: the API already sees the browser's ip, and a custom header would add a CORS preflight
	const visitor_ip = has_ssr() ? await get_ip_server() : null;
	// storefront dev mode (jument): the live site's replica answers, from the API the developer chose; a per-request
	// `uri` wins over the link's own (Apollo's selectURI). Nothing is added for a visitor without the dev cookie
	const dev_mode = await get_dev_mode();
	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : "",
			"site-url": await detect_site_url(),
			"device-type": (await is_mobile_device()) ? "mobile" : "desktop",
			...(visitor_ip ? { "x-visitor-ip": visitor_ip } : {}),
			...dev_mode_headers(dev_mode),
		},
		...(dev_mode?.api_url ? { uri: dev_mode.api_url } : {}),
	};
});

let cached_apollo;

const create_apollo_client = async () => {
	const lang = await get_current_locate_from_cookie();

	// On the server this client is a process-wide singleton shared by every
	// request, so its InMemoryCache must not be read from or written to:
	// it is keyed by query + variables only, which ignores the `device-type`
	// and `authorization` headers, and it has neither a TTL nor eviction.
	// Server-side caching is handled by @core/helpers/server/cache instead.
	const server_side = has_ssr();

	if (cached_apollo && cached_apollo.lang === lang) {
		return cached_apollo;
	}

	const headers = { lang };
	cached_apollo = new ApolloClient({
		link: main_link.concat(
			(await is_localhost()) ? http_link(headers) : link_chain(headers)
		),
		cache: new InMemoryCache({
			addTypename: false,
		}),
		// Server only: Apollo's request dedup key is query + variables and ignores the `authorization` header,
		// so concurrent identical queries from different users would share one response (chawk-bazar, ciseco).
		...(server_side ? { queryDeduplication: false } : {}),
		defaultOptions: {
			query: {
				fetchPolicy: server_side ? "no-cache" : "cache-first",
			},
			mutate: {
				fetchPolicy: server_side ? "no-cache" : "network-only",
			},
			watchQuery: {
				fetchPolicy: "cache-and-network",
				// nextFetchPolicy: 'cache-and-network',
				pollInterval: 30 * 1000, // 30 sec
			},
		},
	});
	cached_apollo.lang = lang;
	return cached_apollo;
};

export default create_apollo_client;
